import proj4 from 'proj4';

// UTM zone to use when converting location easting/northing to WGS84 lat/lon.
// Adjust this constant to match the UTM zone of the mine site deployment.
export const DEFAULT_UTM_ZONE = 43;

const WGS84 = '+proj=longlat +datum=WGS84 +no_defs';

// proj4(from, to, coord) re-parses both projection definitions on every call —
// for a site GeoJSON with hundreds of thousands of coordinate pairs, that
// dwarfs the actual transform math and can block the main thread for seconds
// on a single load. Caching the built converter (proj4(from, to)) and reusing
// its .forward()/.inverse() is the same math ~15x faster.
const converterCache = new Map<string, proj4.Converter>();

const getConverter = (from: string, to: string): proj4.Converter => {
  const key = `${from}|${to}`;
  let converter = converterCache.get(key);
  if (!converter) {
    converter = proj4(from, to);
    converterCache.set(key, converter);
  }
  return converter;
};

export interface LatLon {
  latitude: number;
  longitude: number;
}

export interface UtmCoords {
  easting: number;
  northing: number;
}

export function utmToLatLon(
  easting: number,
  northing: number,
  proj4String?: string | null,
  localOriginEasting?: number | null,
  localOriginNorthing?: number | null,
  _zone: number = DEFAULT_UTM_ZONE,
  _northernHemisphere = true
): LatLon | null {
  if (!isFinite(easting) || !isFinite(northing)) {
    return null;
  }
  // For raw UTM (no proj4String), 0,0 is off-Earth — reject it.
  // For local coordinates (proj4String set), 0,0 is the mine's local origin — allow it.
  if (!proj4String && (easting === 0 || northing === 0)) {
    return null;
  }
  try {
    let longitude: number;
    let latitude: number;

    if (proj4String) {
      const absEasting = easting + (localOriginEasting ?? 0);
      const absNorthing = northing + (localOriginNorthing ?? 0);
      [longitude, latitude] = getConverter(proj4String, WGS84).forward([
        absEasting,
        absNorthing,
      ]);
    } else {
      const utmProj = `+proj=utm +zone=${DEFAULT_UTM_ZONE} +north +datum=WGS84 +units=m +no_defs`;
      [longitude, latitude] = getConverter(utmProj, WGS84).forward([
        easting,
        northing,
      ]);
    }

    if (
      latitude < -90 ||
      latitude > 90 ||
      longitude < -180 ||
      longitude > 180
    ) {
      return null;
    }
    return { latitude, longitude };
  } catch {
    return null;
  }
}

export function latLonToUtm(
  latitude: number,
  longitude: number,
  proj4String?: string | null,
  localOriginEasting?: number | null,
  localOriginNorthing?: number | null,
  _zone: number = DEFAULT_UTM_ZONE,
  _northernHemisphere = true
): UtmCoords | null {
  if (
    !isFinite(latitude) ||
    !isFinite(longitude) ||
    latitude < -90 ||
    latitude > 90 ||
    longitude < -180 ||
    longitude > 180
  ) {
    return null;
  }
  try {
    if (proj4String) {
      const [east, north] = getConverter(WGS84, proj4String).forward([
        longitude,
        latitude,
      ]);
      return {
        easting: Math.round((east - (localOriginEasting ?? 0)) * 100) / 100,
        northing: Math.round((north - (localOriginNorthing ?? 0)) * 100) / 100,
      };
    }
    const utmProj = `+proj=utm +zone=${DEFAULT_UTM_ZONE} +north +datum=WGS84 +units=m +no_defs`;
    const [east, north] = getConverter(WGS84, utmProj).forward([
      longitude,
      latitude,
    ]);
    return {
      easting: Math.round(east * 100) / 100,
      northing: Math.round(north * 100) / 100,
    };
  } catch {
    return null;
  }
}
